import React, { useEffect, useState } from "react";
import client, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { tp, TL } from "../i18n";
import { Card, Modal, Button, Input, Select, Badge } from "./ui";
import { ShieldCheck, ShieldAlert, Network, Zap, TrendingDown, HandCoins, RefreshCw, Lock, Unlock, ChevronRight, AlertTriangle } from "lucide-react";

const APP_KEYS = ["family_tree", "holdings", "currency", "benefits"];
const APP_SHORT = { family_tree: "Family Tree", benefits: "Benefits", currency: "Currency", holdings: "Holdings" };
const appLbl = (s) => { const m = String(s || "").match(/^(.*?)\s*(\(.*\))\s*$/); return m ? `${TL(m[1])} ${m[2]}` : TL(s); };
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const ago = (ts) => {
  if (!ts) return TL("never");
  const d = new Date(ts); if (isNaN(d)) return TL("never");
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return TL("just now");
  if (s < 3600) return `${Math.floor(s / 60)}${TL("m ago")}`;
  if (s < 86400) return `${Math.floor(s / 3600)}${TL("h ago")}`;
  return `${Math.floor(s / 86400)}${TL("d ago")}`;
};

const SCOPE_LABEL = { fopfo: "FopFo (connected apps)", cluster: "Cluster (same-app units)" };

// Inline step-up gate: renders children only when the scope capability is satisfied.
export function CapGate({ scope, title, children }) {
  const { capRequired, capOk } = useApp();
  const [open, setOpen] = useState(false);
  if (!capRequired(scope) || capOk(scope)) return children;
  return (
    <div className="rounded-md border border-dashed border-black/20 dark:border-white/20 p-5 text-center space-y-3" data-testid={`capgate-${scope}`}>
      <Lock size={22} className="mx-auto opacity-60" />
      <p className="text-sm opacity-80">{title || "This is a connected-instance (FopFo) action."} Enable it for this session to continue.</p>
      <Button onClick={() => setOpen(true)} data-testid={`capgate-unlock-${scope}`}><Unlock size={15} /> Enable {scope === "cluster" ? "Cluster" : "FopFo"}</Button>
      <StepUpModal scope={scope} open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

export function StepUpModal({ scope, open, onClose }) {
  const { capMethod, unlockCap } = useApp();
  const method = capMethod(scope);
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true); setMsg("");
    try {
      await unlockCap(scope, method === "pin" ? { pin } : { password });
      setPin(""); setPassword(""); onClose();
    } catch (e) { setMsg(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Modal open={open} onClose={onClose} title={`Enable ${scope === "cluster" ? "Cluster" : "FopFo"} — step-up`} testid={`stepup-${scope}`}>
      <div className="space-y-3">
        {method === "pin" ? (
          <Input label={TL("Enter your 5-digit PIN")} inputMode="numeric" maxLength={5} value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} data-testid={`stepup-pin-${scope}`} />
        ) : (
          <Input label={TL("Re-enter your account password")} type="password" value={password}
            onChange={(e) => setPassword(e.target.value)} data-testid={`stepup-password-${scope}`} />
        )}
        {msg && <div className="rounded-md bg-[#DA1E28]/10 p-2 text-sm text-[#DA1E28]" data-testid={`stepup-msg-${scope}`}>{msg}</div>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{TL("Cancel")}</Button>
          <Button onClick={submit} disabled={busy || (method === "pin" ? pin.length !== 5 : !password)} data-testid={`stepup-submit-${scope}`}>
            <Unlock size={15} /> Enable
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function SecuritySection({ scope, profile, onSaved }) {
  const { unlockCap, hasCap } = useApp();
  const [method, setMethod] = useState(profile[`${scope}_method`] || "off");
  const [pin, setPin] = useState("");
  const [msg, setMsg] = useState("");
  const [stepUp, setStepUp] = useState(false);
  useEffect(() => { setMethod(profile[`${scope}_method`] || "off"); }, [profile, scope]);
  const save = async () => {
    setMsg("");
    try {
      await client.put("/security/profile", { scope, method, pin: method === "pin" ? pin : undefined });
      setPin(""); setMsg("Saved."); onSaved && onSaved();
    } catch (e) { setMsg(apiErr(e)); }
  };
  return (
    <Card className="space-y-2" data-testid={`sec-section-${scope}`}>
      <div className="flex items-center justify-between">
        <span className="font-head text-base">{SCOPE_LABEL[scope]}</span>
        {method !== "off" && (hasCap(scope)
          ? <Badge color="success"><Unlock size={11} className="inline" /> {TL("enabled this session")}</Badge>
          : <Badge color="warning"><Lock size={11} className="inline" /> {TL("locked")}</Badge>)}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Select label={TL("Enable by")} value={method} onChange={(e) => setMethod(e.target.value)} data-testid={`sec-method-${scope}`}>
          <option value="off">{TL("Single login (no extra step)")}</option>
          <option value="pin">{TL("5-digit PIN")}</option>
          <option value="relogin">{TL("Full sign-on again (password)")}</option>
        </Select>
        {method === "pin" && (
          <Input label={TL("Set 5-digit PIN")} inputMode="numeric" maxLength={5} value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            placeholder={profile[`${scope}_pin_set`] ? "•••••  (set — type to change)" : "e.g. 24680"} data-testid={`sec-pin-${scope}`} />
        )}
      </div>
      {msg && <div className="text-xs opacity-70" data-testid={`sec-msg-${scope}`}>{msg}</div>}
      <div className="flex gap-2">
        <Button variant="ghost" onClick={save} disabled={method === "pin" && pin.length > 0 && pin.length !== 5} data-testid={`sec-save-${scope}`}>{TL("Save")}</Button>
        {method !== "off" && !hasCap(scope) && <Button variant="ghost" onClick={() => setStepUp(true)} data-testid={`sec-enable-${scope}`}><Unlock size={14} /> {TL("Enable now")}</Button>}
      </div>
      <StepUpModal scope={scope} open={stepUp} onClose={() => setStepUp(false)} />
    </Card>
  );
}

const StatChip = ({ on, icon, label, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${on ? "bg-[#24A148]/12 text-[#0E6027] dark:text-[#6FDC8C]" : "bg-black/5 dark:bg-white/10 opacity-60"}`}>
    {icon} {label}
  </span>
);

export default function AutomationCard({ app }) {
  const { secProfile, loadSecProfile, isAdmin, lang } = useApp();
  const [d, setD] = useState(null);
  const [details, setDetails] = useState(false);
  const [security, setSecurity] = useState(false);
  const load = () => client.get(`/automation/overview?app=${app}`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [app]);
  if (!d) return null;
  const a = d.automation || {};
  const lowN = d.warnings?.low?.length || 0;
  const highN = d.warnings?.high?.length || 0;
  const controlN = d.clusters.filter((c) => c.role === "Control").length;
  const workerN = d.clusters.filter((c) => c.role === "Worker").length;

  return (
    <Card className="space-y-3" data-testid="automation-card">
      <div className="flex flex-wrap items-center gap-2">
        {(lowN || highN) ? <ShieldAlert size={18} className="text-[#F1C21B]" /> : <ShieldCheck size={18} className="text-[var(--primary)]" />}
        <span className="font-head text-lg">FopFo {TL("Automation")}</span>
        <Badge color="info" data-testid="ac-connected-count"><Network size={11} className="inline" /> {d.connected_count} {tp("connected", lang)}</Badge>
        {controlN > 0 && <Badge color="success" data-testid="ac-control-badge">{TL("Control")} ×{controlN}</Badge>}
        {workerN > 0 && <Badge data-testid="ac-worker-badge">{TL("Worker")} ×{workerN}</Badge>}
        <span className="ms-auto text-xs opacity-60" data-testid="ac-last-used">{tp("Last used", lang)}: {ago(d.last_used?.[app])}</span>
        <button onClick={load} className="opacity-50 hover:opacity-100" data-testid="ac-refresh"><RefreshCw size={14} /></button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {d.connected.map((c) => <Badge key={c.key} data-testid={`ac-app-${c.key}`}>{appLbl(c.label)}</Badge>)}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <StatChip on={a.auto_topup?.enabled} icon={<Zap size={12} />} label={`${tp("Auto top-up", lang)}${a.auto_topup?.count ? ` ×${a.auto_topup.count}` : ""}`} testid="ac-chip-topup" />
        <StatChip on={a.pay?.enabled} icon={<HandCoins size={12} />} label={`${tp("Pay", lang)}${a.pay?.groups ? ` · ${a.pay.groups} group(s)` : ""}`} testid="ac-chip-pay" />
        <StatChip on={a.drawdown?.enabled} icon={<TrendingDown size={12} />} label={tp("Draw-down", lang)} testid="ac-chip-drawdown" />
        {lowN > 0 && <Badge color="warning" data-testid="ac-low"><AlertTriangle size={11} className="inline" /> {lowN} {TL("low pool(s)")}</Badge>}
        {highN > 0 && <Badge color="info" data-testid="ac-high">{highN} {TL("high pool(s)")}</Badge>}
      </div>

      <div className="flex gap-2">
        <Button variant="ghost" onClick={() => setDetails(true)} data-testid="ac-details-btn"><ChevronRight size={14} /> {tp("Details", lang)}</Button>
        <Button variant="ghost" onClick={() => { loadSecProfile(); setSecurity(true); }} data-testid="ac-security-btn"><ShieldCheck size={14} /> {tp("Security profile", lang)}</Button>
      </div>

      {/* Details modal */}
      <Modal open={details} onClose={() => setDetails(false)} title={TL("Automation & connections — details")} testid="ac-details-modal" wide>
        <div className="space-y-4 text-sm">
          <div>
            <div className="mb-1 font-head text-base">{TL("Connected applications")} ({d.connected_count})</div>
            <div className="flex flex-wrap gap-1.5">{d.connected.length ? d.connected.map((c) => <Badge key={c.key}>{appLbl(c.label)}</Badge>) : <span className="opacity-60">{TL("None unlocked.")}</span>}</div>
          </div>
          <div>
            <div className="mb-1 font-head text-base">{TL("Clusters (same-app units)")}</div>
            {d.clusters.length === 0 ? <span className="opacity-60">{TL("Not part of any cluster.")}</span> : (
              <div className="space-y-1">
                {d.clusters.map((c, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 rounded-md bg-black/[0.03] dark:bg-white/[0.06] px-2 py-1" data-testid={`ac-cluster-${i}`}>
                    <Badge color={c.role === "Control" ? "success" : "neutral"}>{TL(c.role)}</Badge>
                    <span className="font-medium">{c.name || TL("Cluster")}</span>
                    <span className="opacity-60">{c.app_label}</span>
                    {c.members != null && <span className="text-xs opacity-60">· {c.members} {TL("member(s)")}</span>}
                    {c.status && <span className="ms-auto text-xs opacity-50">{c.status}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="mb-1 font-head text-base">{TL("Auto top-up pools")}</div>
            {a.auto_topup?.pools?.length ? a.auto_topup.pools.map((p, i) => (
              <div key={i} className="flex flex-wrap gap-x-4 rounded-md bg-black/[0.03] dark:bg-white/[0.06] px-2 py-1" data-testid={`ac-topup-pool-${i}`}>
                <span className="font-medium">{p.name}</span>
                <span className="opacity-70">≤ {money(p.threshold)} → +{money(p.amount)}</span>
                <span className="ms-auto">{TL("bal")} {money(p.balance)}</span>
              </div>
            )) : <span className="opacity-60">{TL("No pools set to auto top-up.")}</span>}
          </div>
          {(lowN > 0 || highN > 0) && (
            <div>
              <div className="mb-1 font-head text-base">{TL("Warnings")}</div>
              {d.warnings.low.map((p, i) => <div key={`l${i}`} className="text-[#DA1E28]" data-testid={`ac-warn-low-${i}`}>⚠ {p.name}: {money(p.balance)} ({TL("below")} {money(p.threshold)})</div>)}
              {d.warnings.high.map((p, i) => <div key={`h${i}`} className="text-[var(--primary)]" data-testid={`ac-warn-high-${i}`}>▲ {p.name}: {money(p.balance)} ({TL("above")} {money(p.high_mark)})</div>)}
            </div>
          )}
          <div>
            <div className="mb-1 font-head text-base">{TL("Recent auto top-ups")}</div>
            {d.recent_topups?.length ? d.recent_topups.map((t, i) => (
              <div key={i} className="opacity-80" data-testid={`ac-recent-topup-${i}`}>+{money(t.amount)} → {t.currency_name} {TL("from")} {t.source} · {ago(t.at)}</div>
            )) : <span className="opacity-60">{TL("None yet.")}</span>}
          </div>
          <div>
            <div className="mb-1 font-head text-base">{TL("Last used")}</div>
            <div className="flex flex-wrap gap-3">
              {APP_KEYS.map((k) => <span key={k} className="text-xs opacity-70">{TL(APP_SHORT[k])}: {ago(d.last_used?.[k])}</span>)}
            </div>
          </div>
        </div>
      </Modal>

      {/* Security profile modal */}
      <Modal open={security} onClose={() => setSecurity(false)} title={TL("Security profile — single vs multiple login")} testid="ac-security-modal" wide>
        <div className="space-y-3">
          <p className="text-sm opacity-70">
            {TL("Choose how you enable connected features after your normal login. Use a quick 5-digit PIN or a full sign-on again — one setting for FopFo (cross-app), one for Cluster (same-app units). “Single login” means no extra step.")}
          </p>
          <SecuritySection scope="fopfo" profile={secProfile} onSaved={loadSecProfile} />
          <SecuritySection scope="cluster" profile={secProfile} onSaved={loadSecProfile} />
          {!isAdmin && <p className="text-xs opacity-50">{TL("Note: Cluster operations are available to administrators; the Cluster setting still applies to your session.")}</p>}
        </div>
      </Modal>
    </Card>
  );
}
